import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { api, tl, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const empty = { name: "", role: "", phone: "", daily_wage: 0, note: "" };

export default function Personnel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/personnel").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const start = (p) => { if (p) { setEdit(p.id); setForm(p); } else { setEdit(null); setForm(empty); } setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Ad zorunlu");
    const payload = { ...form, daily_wage: Number(form.daily_wage) || 0 };
    try {
      if (edit) await api.put(`/personnel/${edit}`, payload);
      else await api.post("/personnel", payload);
      toast.success(edit ? "Güncellendi" : "Eklendi"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/personnel/${id}`); load(); };

  return (
    <div data-testid="personnel-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#d4af37]">Personel</p>
          <h1 className="mt-2 font-display font-extrabold tracking-tighter text-4xl md:text-5xl">Usta & Personel Listesi</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={() => start(null)} data-testid="personnel-add" className="inline-flex items-center gap-2 bg-[#d4af37] text-[#121212] hover:bg-white px-5 py-3 font-display uppercase tracking-[0.18em] text-xs transition">
              <Plus className="h-4 w-4" /> Yeni Personel
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#d4af37]/20 text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-tight">{edit ? "Düzenle" : "Personel Ekle"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Ad Soyad *"><input data-testid="personnel-input-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="kc-input" required /></Field>
                <Field label="Görev"><input value={form.role} onChange={(e)=>setForm({...form,role:e.target.value})} className="kc-input" /></Field>
                <Field label="Telefon"><input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="kc-input" /></Field>
                <Field label="Yevmiye (₺)"><input type="number" step="0.01" value={form.daily_wage} onChange={(e)=>setForm({...form,daily_wage:e.target.value})} className="kc-input" /></Field>
              </div>
              <Field label="Not"><textarea rows={2} value={form.note} onChange={(e)=>setForm({...form,note:e.target.value})} className="kc-input resize-none" /></Field>
              <button data-testid="personnel-save" className="w-full bg-[#d4af37] text-[#121212] hover:bg-white py-3 font-display uppercase tracking-[0.2em] text-xs transition">Kaydet</button>
            </form>
            <KCStyle />
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 overflow-x-auto border border-white/8">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white/55 font-mono text-[10px] uppercase tracking-[0.2em]">
            <tr><th className="text-left p-3">Ad</th><th className="text-left p-3">Görev</th><th className="text-left p-3">Telefon</th><th className="text-right p-3">Yevmiye</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-white/40">Kayıt yok</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/5 transition" data-testid={`personnel-row-${p.id}`}>
                <td className="p-3 text-[#d4af37]">{p.name}</td>
                <td className="p-3">{p.role || "-"}</td>
                <td className="p-3">{p.phone || "-"}</td>
                <td className="p-3 text-right">{tl(p.daily_wage)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => start(p)} className="p-2 text-white/40 hover:text-[#d4af37]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p.id)} className="p-2 text-white/40 hover:text-[#d4af37]"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 mb-2">{label}</div>{children}</label>
);
const KCStyle = () => <style>{`.kc-input{width:100%;background:transparent;border:1px solid rgba(255,255,255,0.12);padding:10px 12px;color:white;outline:none}.kc-input:focus{border-color:#d4af37}`}</style>;
